// A dropdown you can type into: for long lists (countries, health insurers, provinces, clinicians,
// colleagues, medications). Click or focus shows the whole list, typing filters it
// (utils/searchOptions.ts), arrow keys + Enter pick, Escape closes without changing anything.
// An option with value '' is the "nothing selected" entry (its label is the placeholder).
// components/Select.tsx switches to this automatically for lists longer than SEARCHABLE_FROM.
import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Search } from 'lucide-react'
import { cn } from '../utils/cn'
import { filterOptions, SearchOption } from '../utils/searchOptions'

/** Lists with more entries than this become searchable (Select.tsx). */
export const SEARCHABLE_FROM = 12

interface SearchableSelectProps {
  id?: string
  label?: string
  error?: string
  options: SearchOption[]
  value: string
  onChange: (value: string) => void
  /** Shown while nothing is selected; defaults to the label of the '' option */
  placeholder?: string
  disabled?: boolean
  /** Classes of the text field (patient form: input-field; admin pages pass their own) */
  inputClassName?: string
  className?: string
  'aria-label'?: string
}

export function SearchableSelect({
  id, label, error, options, value, onChange, placeholder, disabled, inputClassName = 'input-field', className, 'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const { t } = useTranslation()
  const autoId = useId()
  const inputId = id ?? `ss-${autoId}`
  const listId = `${inputId}-listbox`
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const wrapper = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLUListElement>(null)

  const selected = options.find(o => o.value === value)
  const emptyOption = options.find(o => o.value === '')
  const shown = useMemo(() => filterOptions(options, query), [options, query])
  const hint = placeholder ?? emptyOption?.label ?? ''

  // Close on a click outside
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (wrapper.current && !wrapper.current.contains(e.target as Node)) close() }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  })

  // Keep the highlighted entry in view
  useEffect(() => {
    if (!open || !list.current) return
    const el = list.current.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const openList = () => {
    if (disabled) return
    setQuery('')
    const i = options.findIndex(o => o.value === value)
    setActive(i >= 0 ? i : 0)
    setOpen(true)
  }
  function close() { setOpen(false); setQuery('') }
  const pick = (option: SearchOption) => { onChange(option.value); close() }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) { e.preventDefault(); openList(); return }
    if (!open) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, shown.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) pick(shown[active]) }
    else if (e.key === 'Escape') { e.preventDefault(); close() }
    else if (e.key === 'Tab') close()
  }

  return (
    <div className={cn('w-full', className)} ref={wrapper}>
      {label && <label htmlFor={inputId} className="label-text">{label}</label>}
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-label={label ? undefined : ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${inputId}-opt-${active}` : undefined}
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          // Closed: the chosen entry. Open: what the person types (the chosen entry stays as hint).
          value={open ? query : selected && selected.value !== '' ? selected.label : ''}
          placeholder={open ? (selected && selected.value !== '' ? selected.label : t('common.typeToSearch')) : hint}
          onFocus={openList}
          onClick={() => { if (!open) openList() }}
          onChange={e => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true) }}
          onKeyDown={onKeyDown}
          className={cn(inputClassName, 'pr-10', error && 'border-destructive focus:ring-destructive', disabled && 'opacity-60 cursor-not-allowed')}
        />
        {open ? <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />}
        {open && (
          <ul
            id={listId}
            ref={list}
            role="listbox"
            className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-input border border-border bg-white shadow-lg py-1 text-body"
          >
            {shown.length === 0 ? (
              <li className="px-3 py-2 text-small text-muted-foreground">{t('common.noMatches')}</li>
            ) : shown.map((o, i) => (
              <li
                key={`${o.value}-${i}`}
                id={`${inputId}-opt-${i}`}
                data-index={i}
                role="option"
                aria-selected={o.value === value}
                // mousedown, not click: the input must not lose focus (and close the list) first
                onMouseDown={e => { e.preventDefault(); pick(o) }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'px-3 py-2 cursor-pointer',
                  i === active && 'bg-primary/10',
                  o.value === value && 'font-medium',
                  o.value === '' && 'text-muted-foreground',
                )}
              >
                {o.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <p className="mt-1 text-small text-destructive">{error}</p>}
    </div>
  )
}
